import { memo, useContext, useEffect, useEffectEvent, useRef, useState } from 'react'
import { useAuth } from 'react-oidc-context'
import { useParams } from 'react-router'
import { Chart, HighchartsReactRefObject, setHighcharts } from '@highcharts/react'
import { Box, Grid, useTheme } from '@mui/material'
import Highcharts, { PaneBackgroundShapeValue } from 'highcharts/highcharts.src'

import 'highcharts/highcharts-more'
import 'highcharts/modules/accessibility'
import 'highcharts/modules/solid-gauge'

import { AppContext, useHost } from '../../../../providers/app-provider'
import { centralTheme } from '../../../../providers/mui-theme-provider'
import Api from '../../../../utils/api'

import useStyles from './index.styles'

setHighcharts(Highcharts)

const GaugeChart = () => {
  const classes = useStyles
  const theme = useTheme()
  const auth = useAuth()
  const host = useHost()
  const { pageRefreshCounter } = useContext(AppContext)

  const chartRef = useRef<HighchartsReactRefObject>(null)

  const params = useParams()
  const folderId = params.accountId

  const [percentageDeviceOnline, setPercentageDeviceOnline] = useState<number>(0)
  const [gateways, setGateways] = useState<number>(0)
  const [sensors, setSensors] = useState<number>(0)
  const [totalDevices, setTotalDevices] = useState<number>(0)

  const getGaugeData = useEffectEvent(async () => {
    if (!host || !auth.user?.access_token || !folderId) return
    const res = await Api.post(`${host}/api/v2/central/folders/gauge`, { folderIds: [folderId] })
    if (res.ok) {
      setPercentageDeviceOnline(Math.round(res.body.percentageDeviceOnline))
      setGateways(res.body.gateways)
      setSensors(res.body.sensors)
      setTotalDevices(res.body.totalDevices)
    }
  })
  useEffect(() => {
    getGaugeData()
  }, [folderId, host, pageRefreshCounter])

  const chartData = {
    chart: {
      type: 'solidgauge',
      height: 150,
      width: 180,
      // margin between gauge and text inside of it
      margin: [-40, 0, 20, 0],
      // transparent background for the chart
      backgroundColor: 'rgba(0,0,0,0)',
    },
    title: { text: undefined },
    accessibility: {
      enabled: true,
      description: `Device status gauge showing ${percentageDeviceOnline}% of devices are online. Total devices: ${totalDevices}, including ${gateways} gateways and ${sensors} sensors.`,
    },
    pane: {
      startAngle: -90,
      endAngle: 90,
      borderRadius: 0,
      background: [
        {
          outerRadius: '100%',
          innerRadius: '80%',
          backgroundColor: theme.palette.common.white,
          borderWidth: 0,
          shape: 'solid' as PaneBackgroundShapeValue,
        },
      ],
    },
    yAxis: {
      min: 0,
      max: 100,
      // disable weird y-axis line
      lineWidth: 0,
      tickWidth: 0,
      tickLength: 0,
      minorTickLength: 0,
      labels: {
        enabled: false,
      },
      startOnTick: false,
      endOnTick: false,
    },
    plotOptions: {
      series: {
        enableMouseTracking: false,
      },
      solidgauge: {
        borderWidth: 0,
        rounded: false,
      },
    },
    series: [
      {
        animation: false,
        data: [
          {
            color: '#E0E8F5',
            radius: '100%',
            innerRadius: '80%',
            y: 100,
          },
        ],
        dataLabels: {
          enabled: false,
        },
      },
      {
        data: [
          {
            color: centralTheme.palette.blue.variant3,
            radius: '100%',
            innerRadius: '80%',
            y: percentageDeviceOnline,
          },
        ],
        dataLabels: {
          borderWidth: 0,
          color: centralTheme.palette.blue.variant3,
          x: 0,
          y: -25,
          align: 'center',
          useHTML: true,
          formatter() {
            return `<div style="font-size: 20px; text-align: center; font-weight: 500; letter-spacing: 0.15px;">${`${this.y}%`}</div>
            <div style="font-size: 14px; text-align: center; font-weight: 400; letter-spacing: 0.17px; color: ${theme.palette.secondary.light};">Online</div>`
          },
        },
      },
    ],
    tooltip: { enabled: false },
    credits: { enabled: false },
    exporting: { enabled: false },
  }

  Highcharts.setOptions({
    chart: {
      style: {
        fontFamily: "'Work Sans', sans-serif",
        fontSize: '16px',
        fontWeight: '400',
      },
      events: {
        // disables the ability to click and drag zoom
        selection: () => false,
      },
    },
  })

  return (
    <Box sx={classes.gaugeContainer}>
      <Chart ref={chartRef} highcharts={Highcharts} options={chartData} />
      <Grid container={true} sx={classes.gaugeFooter} size={9}>
        <Grid size={4}>
          <div style={classes.gaugeFooterCount(theme)}>{gateways}</div>
          <div style={classes.gaugeFooterLabel(theme)}>Gateways</div>
        </Grid>

        <Grid size={4}>
          <div style={classes.gaugeFooterCount(theme)}>{sensors}</div>
          <div style={classes.gaugeFooterLabel(theme)}>Sensors</div>
        </Grid>

        <Grid size={4}>
          <div style={classes.gaugeFooterCount(theme)}>{totalDevices}</div>
          <div style={classes.gaugeFooterLabel(theme)}>Total</div>
        </Grid>
      </Grid>
    </Box>
  )
}

export default memo(GaugeChart)
